import type { ActivityStatus, ActivityType } from '@/types';

/**
 * Fixed identity palette.
 *
 * These hues encode data (platform, channel, calendar category), so they must not
 * flip between light and dark like the charcoal theme tokens do. Levels are the ones
 * verified at >= 4.5:1 against white text in both themes — do not lower them.
 */
export const TAG_SWATCHES = [
  'slate',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'indigo',
  'violet',
  'pink',
] as const;

export type TagSwatch = (typeof TAG_SWATCHES)[number];

/** Solid pill fill + white text. */
export const TAG_FILL: Record<TagSwatch, string> = {
  slate: 'bg-slate-600 text-white',
  red: 'bg-red-600 text-white',
  orange: 'bg-orange-700 text-white',
  amber: 'bg-amber-700 text-white',
  green: 'bg-green-700 text-white',
  teal: 'bg-teal-700 text-white',
  blue: 'bg-blue-600 text-white',
  indigo: 'bg-indigo-600 text-white',
  violet: 'bg-violet-600 text-white',
  pink: 'bg-pink-600 text-white',
};

/** Solid background only, at the same text-safe level — pair with white text. */
export const TAG_BG: Record<TagSwatch, string> = {
  slate: 'bg-slate-600',
  red: 'bg-red-600',
  orange: 'bg-orange-700',
  amber: 'bg-amber-700',
  green: 'bg-green-700',
  teal: 'bg-teal-700',
  blue: 'bg-blue-600',
  indigo: 'bg-indigo-600',
  violet: 'bg-violet-600',
  pink: 'bg-pink-600',
};

/**
 * Bare marks (legend dots, rails) carry no text, so they are graded against the
 * surface instead: the darker swatches drop under 3:1 on the dark card, so they
 * lift a step there. Hue is preserved — only lightness moves.
 */
export const TAG_DOT: Record<TagSwatch, string> = {
  slate: 'bg-slate-600 dark:bg-slate-400',
  red: 'bg-red-600 dark:bg-red-500',
  orange: 'bg-orange-700 dark:bg-orange-500',
  amber: 'bg-amber-700 dark:bg-amber-500',
  green: 'bg-green-700 dark:bg-green-500',
  teal: 'bg-teal-700 dark:bg-teal-500',
  blue: 'bg-blue-600 dark:bg-blue-400',
  indigo: 'bg-indigo-600 dark:bg-indigo-400',
  violet: 'bg-violet-600 dark:bg-violet-400',
  pink: 'bg-pink-600 dark:bg-pink-500',
};

/** Raw hex, for charts and timelines that cannot take a class. */
export const TAG_HEX: Record<TagSwatch, string> = {
  slate: '#475569',
  red: '#dc2626',
  orange: '#c2410c',
  amber: '#b45309',
  green: '#15803d',
  teal: '#0f766e',
  blue: '#2563eb',
  indigo: '#4f46e5',
  violet: '#7c3aed',
  pink: '#db2777',
};

/**
 * Activity type is a colour key on calendars, legends and timelines, so each
 * type needs its own hue. `other` stays neutral — it is the absence of a type.
 */
export const ACTIVITY_TYPE_SWATCH: Record<Exclude<ActivityType, 'other'>, TagSwatch> = {
  google_ads: 'blue',
  linkedin_ads: 'indigo',
  email: 'green',
  campaign: 'orange',
  social: 'pink',
  event: 'amber',
  content: 'teal',
  seo: 'violet',
  webinar: 'red',
  print: 'slate',
};

/** Solid background for a calendar/plan chip that renders white text. */
export function activityTypeBg(type: ActivityType): string {
  return type === 'other' ? 'bg-muted' : TAG_BG[ACTIVITY_TYPE_SWATCH[type]];
}

/** Bare legend dot for a type — no text on top. */
export function activityTypeDot(type: ActivityType): string {
  return type === 'other' ? 'bg-muted' : TAG_DOT[ACTIVITY_TYPE_SWATCH[type]];
}

export function activityTypeFill(type: ActivityType): string {
  return type === 'other' ? 'bg-muted text-primary-foreground' : TAG_FILL[ACTIVITY_TYPE_SWATCH[type]];
}

export function activityTypeHex(type: ActivityType): string {
  return type === 'other' ? '#6e6862' : TAG_HEX[ACTIVITY_TYPE_SWATCH[type]];
}

/** 10% wash of the swatch — board column headers, which render foreground text. */
export const TAG_TINT: Record<TagSwatch, string> = {
  slate: 'bg-slate-600/10',
  red: 'bg-red-600/10',
  orange: 'bg-orange-700/10',
  amber: 'bg-amber-700/10',
  green: 'bg-green-700/10',
  teal: 'bg-teal-700/10',
  blue: 'bg-blue-600/10',
  indigo: 'bg-indigo-600/10',
  violet: 'bg-violet-600/10',
  pink: 'bg-pink-600/10',
};

/** Workflow status reads as an ordered colour scale on boards and Gantt bars. */
export const ACTIVITY_STATUS_SWATCH: Record<ActivityStatus, TagSwatch> = {
  backlog: 'slate',
  todo: 'blue',
  in_progress: 'amber',
  review: 'violet',
  done: 'green',
};

/** Pill chrome shared by every tag. */
export const TAG_BASE =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';

export function tagClass(swatch: TagSwatch): string {
  return `${TAG_BASE} ${TAG_FILL[swatch]}`;
}

/** Stable swatch for arbitrary keys (tags, labels, avatars). */
export function tagSwatchFor(key: string): TagSwatch {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return TAG_SWATCHES[hash % TAG_SWATCHES.length] ?? 'slate';
}
